import Toast from "react-native-toast-message";
import { supabase } from "./client";
import { getCurrentOrgID } from "./organization";
import ErrorHandler from "@/utils/ErrorHandler";



/**
 * Retrieves all org-wide profile views, ordered by name.
 * @returns {Promise<object[]>} List of profile views.
 */
const getProfileViews = async () => {
    const orgID = await getCurrentOrgID();
    if (!orgID) return [];

    const { data, error } = await supabase.from("ProfileViews").select("*").eq("organizationID", orgID).order('name');

    if (error) {
        ErrorHandler(Error(error.message), { query: "getProfileViews", orgID, ...error }, `Something went wrong fetching the profile views`)
        return [];
    }

    return data;
}

/**
 * Retrieves a single profile view by ID.
 * @param {string} id - The profile view ID.
 * @returns {Promise<object|null>} The profile view or null on error.
 */
const getProfileView = async (id) => {
    const orgID = await getCurrentOrgID();
    if (!orgID) return null;

    const { data, error } = await supabase.from("ProfileViews").select("*").eq("organizationID", orgID).eq("id", id).maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), { query: "getProfileView", orgID, id, ...error }, `Something went wrong fetching the profile view`)
        return null;
    }

    return data;
}

/**
 * Creates a new profile view.
 * @param {object} params - The parameters for the function.
 * @param {string} params.name - Display name of the view.
 * @param {object} params.config - JSON config selecting which fields the card renders.
 * @param {string} [params.createdByPersonID] - Person ID of the creator.
 * @returns {Promise<object|boolean>} The created view or false on error.
 */
const createProfileView = async ({ name, config, createdByPersonID }) => {
    const orgID = await getCurrentOrgID();

    const insert = {
        organizationID: orgID,
        name,
        config: config || {},
        createdByPersonID: createdByPersonID || null,
    }

    const { data, error } = await supabase.from("ProfileViews").insert(insert).select().single();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "createProfileView", input: insert, ...error }, `Something went wrong creating that profile view`)
        return false;
    }

    Toast.show({
        type: 'success',
        text2: `Profile view has been created successfully!`
    });

    return data;
}

/**
 * Updates an existing profile view.
 * @param {object} params - The parameters for the function.
 * @param {string} params.id - The profile view ID.
 * @param {string} params.name - Display name of the view.
 * @param {object} params.config - JSON config selecting which fields the card renders.
 * @returns {Promise<object|boolean>} The updated view or false on error.
 */
const updateProfileView = async ({ id, name, config }) => {
    const update = {
        name,
        config: config || {},
        updatedAt: new Date().toISOString(),
    }

    const { data, error } = await supabase.from("ProfileViews").update(update).eq("id", id).select().single();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "updateProfileView", id, input: update, ...error }, `Something went wrong updating that profile view`)
        return false;
    }

    Toast.show({
        type: 'success',
        text2: `Profile view has been updated successfully!`
    });

    return data;
}

/**
 * Deletes a profile view by ID.
 * @param {string} id - The ID of the profile view to delete.
 * @returns {Promise<boolean|void>} False on error, else void.
 */
const deleteProfileView = async (id) => {
    const { error } = await supabase.from("ProfileViews").delete().eq("id", id);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "deleteProfileView", input: id, ...error }, `Something went wrong deleting that profile view`)
        return false;
    }

    Toast.show({
        type: 'success',
        text2: `Profile view has been deleted successfully!`
    });
}

/**
 * Fetches the rendered card payload for a profile view + person pair.
 * Section keys the viewer can't see (or that aren't configured) are omitted
 * server-side — the card renders only the keys present.
 *
 * Passing `config` renders an unsaved draft config (the saved-view lookup is
 * skipped and `view` in the response may be {id: null, name: null, config}).
 * @param {object} params - The parameters for the function.
 * @param {string} [params.viewID] - The profile view ID (omit when previewing an unsaved config).
 * @param {string} params.personID - The person ID to render the card for.
 * @param {object} [params.config] - Draft config to render instead of the saved view's.
 * @returns {Promise<object|null>} { view, person, sections } or null on error.
 */
const getProfileViewCard = async ({ viewID, personID, config }) => {
    const { data, error } = await supabase.rpc('get_profile_view_card', {
        p_view_id: viewID || null,
        p_person_id: personID,
        p_config: config || null,
    });

    if (error) {
        ErrorHandler(Error(error.message), { query: "getProfileViewCard", viewID, personID, ...error }, `Something went wrong loading the profile view preview`)
        return null;
    }

    return data;
}

export {
    getProfileViews,
    getProfileView,
    createProfileView,
    updateProfileView,
    deleteProfileView,
    getProfileViewCard,
}
